import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PromoCampaignListItem } from '../../../shared/services/api.service';

export interface CampaignDialogData {
  mode: 'create' | 'edit';
  campaign?: PromoCampaignListItem;
}

export interface CampaignDialogResult {
  name: string;
  maxUsers: number;
  expiresAt: string | null;
}

@Component({
  selector: 'app-campaign-dialog',
  templateUrl: './campaign-dialog.component.html',
  styleUrls: ['./campaign-dialog.component.css'],
})
export class CampaignDialogComponent implements OnInit {
  form: FormGroup;
  readonly minExpiresDate = new Date();

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<CampaignDialogComponent, CampaignDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: CampaignDialogData
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(120)]],
      maxUsers: [100, [Validators.required, Validators.min(1)]],
      expiresAt: [null as Date | null],
    });
  }

  ngOnInit(): void {
    if (this.data.mode === 'edit' && this.data.campaign) {
      this.form.patchValue({
        name: this.data.campaign.name,
        maxUsers: this.data.campaign.maxUsers,
        expiresAt: this.toDateValue(this.data.campaign.expiresAt),
      });
    }
  }

  get title(): string {
    return this.data.mode === 'create' ? 'New Campaign' : 'Edit Campaign';
  }

  clearExpiresAt(): void {
    this.form.get('expiresAt')?.setValue(null);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    this.dialogRef.close({
      name: String(raw.name || '').trim(),
      maxUsers: Number(raw.maxUsers),
      expiresAt: this.toEndOfDayIso(raw.expiresAt),
    });
  }

  private toDateValue(value: string | null | undefined): Date | null {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }

  /** Expiry covers the full selected calendar day (local end-of-day). */
  private toEndOfDayIso(value: Date | null | undefined): string | null {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
    const endOfDay = new Date(value.getFullYear(), value.getMonth(), value.getDate(), 23, 59, 59, 999);
    return endOfDay.toISOString();
  }
}
